import { Box, Typography } from '@mui/material';
import { Building2, Mail, Phone } from 'lucide-react';
import { useCreatePath, useListContext, Link, useGetMany } from 'react-admin';

import { Account, Contact } from '../types';

const ContactGridCard = ({
    record,
    accountName,
}: {
    record: Contact;
    accountName?: string;
}) => {
    const createPath = useCreatePath();
    const initials = [record.first_name?.charAt(0), record.last_name?.charAt(0)]
        .filter(Boolean)
        .join('')
        .toUpperCase();

    const primaryEmail = record.email_jsonb?.[0]?.email;
    const primaryPhone = record.phone_jsonb?.[0]?.number;

    return (
        <Link
            to={createPath({
                resource: 'contacts',
                id: record.id,
                type: 'show',
            })}
            underline="none"
            sx={{ display: 'block' }}
        >
            <Box
                sx={{
                    borderRadius: '8px',
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#ffffff',
                    p: 2,
                    cursor: 'pointer',
                    transition: 'box-shadow 0.15s, transform 0.15s',
                    '&:hover': {
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        transform: 'scale(1.02)',
                    },
                }}
            >
                {/* Header: Avatar + Name */}
                <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                    {record.avatar?.src ? (
                        <Box
                            component="img"
                            src={record.avatar.src}
                            alt={`${record.first_name} ${record.last_name}`}
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                flexShrink: 0,
                            }}
                        />
                    ) : (
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: '50%',
                                backgroundColor: '#dbeafe',
                                color: '#1d4ed8',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                flexShrink: 0,
                            }}
                        >
                            {initials}
                        </Box>
                    )}
                    <Box sx={{ minWidth: 0 }}>
                        <Typography
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.9375rem',
                                color: '#0f172a',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {record.first_name} {record.last_name}
                        </Typography>
                        {record.title && (
                            <Typography
                                sx={{
                                    fontSize: '0.75rem',
                                    color: '#64748b',
                                }}
                            >
                                {record.title}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Info rows */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.75,
                        mb: 1.5,
                    }}
                >
                    {primaryPhone && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Phone size={14} color="#94a3b8" />
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#475569',
                                }}
                            >
                                {primaryPhone}
                            </Typography>
                        </Box>
                    )}
                    {primaryEmail && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Mail size={14} color="#94a3b8" />
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#475569',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {primaryEmail}
                            </Typography>
                        </Box>
                    )}
                    {accountName && (
                        <Box display="flex" alignItems="center" gap={1}>
                            <Building2 size={14} color="#94a3b8" />
                            <Typography
                                sx={{
                                    fontSize: '0.8125rem',
                                    color: '#475569',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {accountName}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Link>
    );
};

export const ContactCardGrid = () => {
    const { data, isPending } = useListContext<Contact>();

    // Collect unique account IDs to batch-fetch names
    const accountIds = Array.from(
        new Set((data ?? []).map(c => c.account_id).filter(Boolean))
    );

    const { data: accounts } = useGetMany<Account>(
        'accounts',
        { ids: accountIds },
        { enabled: accountIds.length > 0 }
    );

    const accountMap = new Map((accounts ?? []).map(a => [a.id, a.name]));

    if (isPending || !data) return null;

    if (data.length === 0) {
        return (
            <Box py={4} textAlign="center">
                <Typography
                    sx={{
                        color: '#64748b',
                        fontSize: '0.875rem',
                    }}
                >
                    No contacts found
                </Typography>
            </Box>
        );
    }

    return (
        <Box
            display="grid"
            gridTemplateColumns={{
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
            }}
            gap={2}
        >
            {data.map(record => (
                <ContactGridCard
                    key={record.id}
                    record={record}
                    accountName={
                        record.account_id
                            ? accountMap.get(record.account_id)
                            : undefined
                    }
                />
            ))}
        </Box>
    );
};
